import axios from 'axios'
import type { Agent, AgentPermission, AgentPermissionsInfo, Visibility, PermissionLevel } from '../types/agent'
import type { TeamTemplate, TeamRole, TeamInstance, TeamAgentAssignment } from '../types/team'

const API_BASE_URL = '/api'

// Configuration d'axios avec intercepteur pour l'authentification
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Intercepteur pour ajouter le token d'authentification
apiClient.interceptors.request.use((config) => {
  const authStorage = localStorage.getItem('auth-storage')
  if (authStorage) {
    try {
      const authData = JSON.parse(authStorage)
      // * Zustand persist stocke les données dans state
      const tokenValue = authData.state?.token
      if (tokenValue) {
        config.headers.Authorization = `Bearer ${tokenValue}`
      } else {
        console.warn('Token non trouvé dans auth-storage')
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du token:', error)
    }
  } else {
    console.warn('auth-storage non trouvé dans localStorage')
  }
  return config
})

// Intercepteur de réponse pour gérer les erreurs 401 (non authentifié)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // * Token invalide ou expiré
      const currentPath = window.location.pathname
      if (currentPath !== '/login' && currentPath !== '/auth/callback') {
        console.warn('Session expirée, redirection vers la page de login')
        // * Nettoyer le localStorage et rediriger
        localStorage.removeItem('auth-storage')
        // * Utiliser window.location.href pour forcer un rechargement complet
        window.location.href = '/login?error=session_expired'
      }
    }
    return Promise.reject(error)
  }
)

// * Types are now imported from '../types/agent'
// * Re-export for backward compatibility
export type { Agent, AgentPermission, AgentPermissionsInfo, Visibility, PermissionLevel } from '../types/agent'

// * Team types are now imported from '../types/team'

export const agentService = {
  getAllAgents: async (): Promise<Agent[]> => {
    const response = await apiClient.get('/agents')
    return response.data.data
  },

  getAgentById: async (id: string): Promise<Agent> => {
    const response = await apiClient.get(`/agents/${id}`)
    return response.data.data
  },

  createAgent: async (agentData: Partial<Agent> & { skills?: string[] }): Promise<Agent> => {
    const response = await apiClient.post('/agents', agentData)
    return response.data.data
  },

  updateAgent: async (id: string, agentData: Partial<Agent> & { skills?: string[] }): Promise<Agent> => {
    const response = await apiClient.put(`/agents/${id}`, agentData)
    return response.data.data
  },

  deleteAgent: async (id: string): Promise<void> => {
    await apiClient.delete(`/agents/${id}`)
  },

  deployAgent: async (id: string): Promise<any> => {
    const response = await apiClient.post(`/agents/${id}/deploy`)
    return response.data.data
  },

  stopAgent: async (id: string): Promise<void> => {
    await apiClient.post(`/agents/${id}/stop`)
  },

  // * Hibernate agent (stop container without removing)
  hibernateAgent: async (id: string): Promise<void> => {
    await apiClient.post(`/agents/${id}/hibernate`)
  },

  // * Resume agent (start container)
  resumeAgent: async (id: string): Promise<void> => {
    await apiClient.post(`/agents/${id}/resume`)
  },

  // * Delete agent instance (unregister and remove container)
  deleteAgentInstance: async (id: string): Promise<void> => {
    await apiClient.delete(`/agents/${id}/instance`)
  },

  // * Get agent metrics
  getAgentMetrics: async (agentIds: string[], startTime: Date, endTime: Date): Promise<any[]> => {
    const response = await apiClient.get('/agents/metrics', {
      params: {
        agentIds: agentIds.join(','),
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString()
      }
    })
    return response.data.data
  },

  getAgentStatus: async (id: string): Promise<any> => {
    const response = await apiClient.get(`/agents/${id}/status`)
    return response.data.data
  },

  getAgentLogs: async (id: string, lines: number = 100): Promise<string[]> => {
    const response = await apiClient.get(`/agents/${id}/logs?lines=${lines}`)
    return response.data.data
  },

  duplicateAgent: async (id: string, isFork: boolean = false): Promise<Agent> => {
    const response = await apiClient.post(`/agents/${id}/duplicate?fork=${isFork}`)
    return response.data.data
  },

  getActiveAgents: async (): Promise<Agent[]> => {
    const response = await apiClient.get('/agents/active/list')
    return response.data.data
  },

  cleanInactiveAgents: async (): Promise<{ deletedCount: number }> => {
    const response = await apiClient.post('/agents/clean/inactive')
    // * API returns { success: true, message: string, data: { deletedCount: number } }
    return response.data.data || { deletedCount: 0 }
  },

  cleanDuplicateAgents: async (): Promise<{ deletedCount: number }> => {
    const response = await apiClient.post('/agents/clean/duplicates')
    // * API returns { success: true, message: string, data: { deletedCount: number } }
    return response.data.data || { deletedCount: 0 }
  },

  cleanAllAgentInstances: async (): Promise<{ deletedCount: number }> => {
    const response = await apiClient.post('/agents/clean/instances')
    // * API returns { success: true, message: string, data: { deletedCount: number } }
    return response.data.data || { deletedCount: 0 }
  },

  cleanAllAgentModels: async (): Promise<{ deletedCount: number }> => {
    const response = await apiClient.post('/agents/clean/models')
    // * API returns { success: true, message: string, data: { deletedCount: number } }
    return response.data.data || { deletedCount: 0 }
  },

  cleanAllAgents: async (): Promise<{ deletedCount: number }> => {
    const response = await apiClient.post('/agents/clean/all')
    // * API returns { success: true, message: string, data: { deletedCount: number } }
    return response.data.data || { deletedCount: 0 }
  },

  // * Deploy agent instance directly
  deployAgentInstance: async (agentId: string, options: { engine?: string; engineVersion?: string; coreUrl?: string; apiKeyIds?: Record<string, string>; teamInstanceId?: string; roleId?: string; selectedModules?: string[]; requirementValues?: Record<string, string> }): Promise<{ instanceId: string }> => {
    const response = await apiClient.post(`/agents/${agentId}/deploy-instance`, options)
    return response.data.data
  },

  // * Download agent deployment package
  downloadAgentDeploymentPackage: async (agentId: string, options: { engine: string; engineVersion?: string; coreUrl?: string; apiKeyIds?: Record<string, string>; selectedModules?: string[]; requirementValues?: Record<string, string> }): Promise<Blob> => {
    const response = await apiClient.post(
      `/agents/${agentId}/download-package`,
      options,
      { responseType: 'blob' }
    )
    return response.data
  },
}

export const teamService = {
  // * Team Templates
  getAllTeamTemplates: async (): Promise<TeamTemplate[]> => {
    const response = await apiClient.get('/teams/templates')
    return response.data.data
  },

  getTeamTemplateById: async (id: string): Promise<TeamTemplate> => {
    const response = await apiClient.get(`/teams/templates/${id}`)
    return response.data.data
  },

  createTeamTemplate: async (teamData: { name: string; description?: string; config?: any; circleKind?: 'human' | 'agent' }): Promise<TeamTemplate> => {
    const response = await apiClient.post('/teams/templates', teamData)
    return response.data.data
  },

  updateTeamTemplate: async (id: string, teamData: Partial<TeamTemplate>): Promise<TeamTemplate> => {
    const response = await apiClient.put(`/teams/templates/${id}`, teamData)
    return response.data.data
  },

  deleteTeamTemplate: async (id: string): Promise<void> => {
    await apiClient.delete(`/teams/templates/${id}`)
  },

  duplicateTeamTemplate: async (id: string): Promise<TeamTemplate> => {
    const response = await apiClient.post(`/teams/templates/${id}/duplicate`)
    return response.data.data
  },

  // * Team Roles
  addTeamRole: async (teamTemplateId: string, roleData: { name: string; description?: string; isUnique: boolean; agentType: 'ai' | 'human' | 'both' }): Promise<TeamRole> => {
    const response = await apiClient.post(`/teams/templates/${teamTemplateId}/roles`, roleData)
    return response.data.data
  },

  updateTeamRole: async (teamTemplateId: string, roleId: string, roleData: Partial<TeamRole>): Promise<TeamRole> => {
    const response = await apiClient.put(`/teams/templates/${teamTemplateId}/roles/${roleId}`, roleData)
    return response.data.data
  },

  deleteTeamRole: async (teamTemplateId: string, roleId: string): Promise<void> => {
    await apiClient.delete(`/teams/templates/${teamTemplateId}/roles/${roleId}`)
  },

  // * Team Instances
  getAllTeamInstances: async (): Promise<TeamInstance[]> => {
    const response = await apiClient.get('/teams/instances')
    return response.data.data
  },

  getTeamInstanceById: async (id: string): Promise<TeamInstance> => {
    const response = await apiClient.get(`/teams/instances/${id}`)
    return response.data.data
  },

  createTeamInstance: async (
    teamTemplateId: string,
    name?: string,
    assignments?: Array<{ agentId: string; sourceRoleId: string }>
  ): Promise<TeamInstance> => {
    const response = await apiClient.post('/teams/instances', { teamTemplateId, name, assignments })
    return response.data.data
  },

  updateTeamInstance: async (id: string, instanceData: { name?: string }): Promise<TeamInstance> => {
    const response = await apiClient.put(`/teams/instances/${id}`, instanceData)
    return response.data.data
  },

  deleteTeamInstance: async (id: string, reassignments?: { agentId: string; teamInstanceId?: string; roleId?: string }[]): Promise<void> => {
    await apiClient.delete(`/teams/instances/${id}`, { data: { reassignments } })
  },

  duplicateTeamInstance: async (id: string): Promise<TeamInstance> => {
    const response = await apiClient.post(`/teams/instances/${id}/duplicate`)
    return response.data.data
  },

  // * Agent Team Assignments
  assignAgentToTeam: async (agentId: string, teamInstanceId: string, roleId: string): Promise<TeamAgentAssignment> => {
    const response = await apiClient.post('/teams/assignments', { agentId, teamInstanceId, roleId })
    return response.data.data
  },

  removeAgentFromTeam: async (agentId: string, teamInstanceId: string): Promise<void> => {
    await apiClient.delete(`/teams/assignments`, { data: { agentId, teamInstanceId } })
  },
}

export interface CanvasRole {
  id: string
  name: string
  purpose: string
  domains: string[]
  accountabilities: string[]
  isLiaison: boolean
  isOrchestrator: boolean
  isUnique?: boolean
  communicationInterne: boolean
  communicationExterne: boolean
  agentType: string
  layoutX: number | null
  layoutY: number | null
  skills: RoleSkillChoice[]
  assignee: { id: string; name: string; kind: 'human' | 'agent' } | null
}

export interface CanvasLink {
  id: string
  sourceRoleId: string
  targetRoleId: string
  kind?: 'internal' | 'external'
}

export interface CanvasCircle {
  id: string
  purpose: string
  kind: 'human' | 'agent'
  implicit: boolean
  x: number
  y: number
  roles: CanvasRole[]
  links: CanvasLink[]
}

export interface CanvasView {
  mode: 'template' | 'instance'
  id: string
  name: string
  circles: CanvasCircle[]
}

export interface RoleSkillChoice {
  name: string
  description: string
  catalogId: string | null
}

export type SkillChannel = 'circle' | 'mail' | 'discord' | 'slack' | 'whatsapp'

export interface CatalogSkill {
  id: string
  name: string
  description: string
  channel: SkillChannel | null
}

export interface CircleResource {
  id: string
  parentId: string | null
  kind: 'folder' | 'document'
  name: string
  body: string | null
  level: 'read' | 'write' | 'manage' | null
  path: string
}

export const circleService = {
  canvas: async (scope: 'template' | 'instance', id: string): Promise<CanvasView> => {
    const response = await apiClient.get(`/teams/canvas/${scope}/${id}`)
    return response.data.data
  },
  createCircle: async (payload: { templateId?: string; instanceId?: string; purpose: string; kind: 'human' | 'agent' }) => {
    const response = await apiClient.post('/teams/circles', payload)
    return response.data.data as { id: string }
  },
  updateCircle: async (id: string, payload: { purpose?: string; x?: number; y?: number }) => {
    await apiClient.patch(`/teams/circles/${id}`, payload)
  },
  deleteCircle: async (id: string) => {
    await apiClient.delete(`/teams/circles/${id}`)
  },
  createRole: async (circleId: string, payload: { name: string; purpose?: string; domains?: string[]; accountabilities?: string[]; skills?: RoleSkillChoice[]; isOrchestrator?: boolean }) => {
    const response = await apiClient.post(`/teams/circles/${circleId}/roles`, payload)
    return response.data.data as { id: string }
  },
  updateRole: async (roleId: string, payload: Record<string, unknown>) => {
    await apiClient.patch(`/teams/roles/${roleId}`, payload)
  },
  deleteRole: async (roleId: string) => {
    await apiClient.delete(`/teams/roles/${roleId}`)
  },
  assign: async (roleId: string, payload: { teamInstanceId: string; agentId?: string; userId?: string }) => {
    await apiClient.post(`/teams/roles/${roleId}/assignment`, payload)
  },
  declaredSkills: async (payload: { agentId?: string; userId?: string }): Promise<string[]> => {
    const response = await apiClient.get('/teams/skills', { params: payload })
    return response.data.data
  },
  saveSkills: async (payload: { agentId?: string; userId?: string; skills: string[] }) => {
    await apiClient.put('/teams/skills', payload)
  },
  catalog: async (): Promise<CatalogSkill[]> => {
    const response = await apiClient.get('/teams/skill-catalog')
    return response.data.data
  },
  createCatalogSkill: async (payload: { name: string; description: string; channel: SkillChannel | '' }) => {
    const response = await apiClient.post('/teams/skill-catalog', {
      name: payload.name,
      description: payload.description,
      channel: payload.channel || null,
    })
    return response.data.data as CatalogSkill
  },
  updateCatalogSkill: async (id: string, payload: { name: string; description: string; channel: SkillChannel | '' }) => {
    const response = await apiClient.patch(`/teams/skill-catalog/${id}`, {
      name: payload.name,
      description: payload.description,
      channel: payload.channel || null,
    })
    return response.data.data as CatalogSkill
  },
  deleteCatalogSkill: async (id: string) => {
    await apiClient.delete(`/teams/skill-catalog/${id}`)
  },
  resources: async (circleId: string, roleId?: string): Promise<CircleResource[]> => {
    const response = await apiClient.get(`/teams/circles/${circleId}/resources`, { params: roleId ? { roleId } : {} })
    return response.data.data
  },
  createResource: async (payload: { circleId: string; kind: 'folder' | 'document'; name: string; body?: string }) => {
    await apiClient.post('/teams/resources', payload)
  },
  setGrant: async (resourceId: string, roleId: string, level: 'read' | 'write' | 'manage') => {
    await apiClient.put(`/teams/resources/${resourceId}/grants`, { roleId, level })
  },
  saveLayout: async (circleId: string, roles: Array<{ id: string; x: number; y: number }>) => {
    await apiClient.put(`/teams/circles/${circleId}/layout`, { roles })
  },
  toggleLink: async (circleId: string, sourceRoleId: string, targetRoleId: string) => {
    await apiClient.post(`/teams/circles/${circleId}/links`, { sourceRoleId, targetRoleId })
  },
  ensureBridge: async (sourceRoleId: string, targetRoleId: string) => {
    await apiClient.post('/teams/links/bridge', { sourceRoleId, targetRoleId })
  },
  moveRole: async (roleId: string, circleId: string, point?: { x: number; y: number }) => {
    await apiClient.post(`/teams/roles/${roleId}/circle`, { circleId, x: point?.x, y: point?.y })
  },
  removeLink: async (linkId: string) => {
    await apiClient.delete(`/teams/links/${linkId}`)
  },
}

export type CatalogKind = 'document' | 'repository' | 'cloud' | 'other'
export type CatalogVisibility = 'private' | 'shared' | 'public'
export type CatalogLevel = 'read' | 'write'

export interface CatalogShare {
  userId?: string
  username: string
  level: CatalogLevel
}

export interface CatalogResource {
  id: string
  name: string
  kind: CatalogKind
  description: string
  locator: string
  visibility: CatalogVisibility
  ownerId: string
  ownerName: string
  shareLevel: CatalogLevel | null
  picked: boolean
  canEdit: boolean
  canDelete: boolean
  owned: boolean
  shares: CatalogShare[]
}

export interface CatalogUse {
  instanceId: string
  instanceName: string
  templateId: string
  roleId: string
  roleName: string
  circleId: string
  circleName: string
  sourceCircleId: string
  sourceRoleId: string
  agentId: string
  agentName: string
}

export interface CatalogGrant {
  resourceId: string
  name: string
  kind: CatalogKind
  scope: 'global' | 'circle' | 'role'
  level: CatalogLevel
  circleId: string | null
  roleId: string | null
}

export interface CatalogBindingView {
  grants: CatalogGrant[]
  circles: Array<{ id: string; name: string }>
  roles: Array<{ id: string; name: string; circleId: string }>
}

export interface CatalogGroups {
  mine: CatalogResource[]
  shared: CatalogResource[]
  public: CatalogResource[]
}

export const catalogService = {
  list: async (): Promise<CatalogGroups> => {
    const response = await apiClient.get('/catalog/resources')
    return response.data.data
  },
  available: async (): Promise<CatalogGroups> => {
    const response = await apiClient.get('/catalog/resources/available')
    return response.data.data
  },
  create: async (payload: { name: string; kind: CatalogKind; description: string; locator: string; visibility: CatalogVisibility; shares: CatalogShare[] }) => {
    const response = await apiClient.post('/catalog/resources', payload)
    return response.data.data as { id: string }
  },
  update: async (id: string, payload: { name: string; kind: CatalogKind; description: string; locator: string; visibility: CatalogVisibility; shares: CatalogShare[]; confirmUsage?: boolean }) => {
    await apiClient.patch(`/catalog/resources/${id}`, payload)
  },
  remove: async (id: string) => {
    await apiClient.delete(`/catalog/resources/${id}`)
  },
  pick: async (id: string, picked: boolean) => {
    await apiClient.put(`/catalog/resources/${id}/pick`, { picked })
  },
  bindings: async (scope: 'template' | 'instance', id: string): Promise<CatalogBindingView> => {
    const path = scope === 'template' ? 'teams' : 'instances'
    const response = await apiClient.get(`/catalog/${path}/${id}/bindings`)
    return response.data.data
  },
  saveBindings: async (scope: 'template' | 'instance', id: string, resourceId: string, grants: Array<Pick<CatalogGrant, 'scope' | 'level' | 'circleId' | 'roleId'>>, confirmUsage = false) => {
    const path = scope === 'template' ? 'teams' : 'instances'
    await apiClient.put(`/catalog/${path}/${id}/bindings`, { resourceId, grants, confirmUsage })
  },
}

export interface EngineRequirementField {
  name: string
  type: 'text' | 'url' | 'secret'
  required: boolean
  description?: string
}

export interface EngineModuleSpec {
  id: string
  description: string
  dependsOn: string[]
  providesSkills: string[]
  defaultEnabled: boolean
  locked: boolean
  requirements: EngineRequirementField[]
}

export interface EngineRequirements {
  engine: string
  version: string
  modules: EngineModuleSpec[]
}

export interface EngineSelection {
  version: string
  modules: EngineModuleSpec[]
  selectedModules: string[]
  pulledBy: Record<string, string[]>
  enabled: Record<string, boolean>
}

export interface EngineManifestRow {
  name: string
  version: string
  kind: string
  description?: string
  compatible: boolean
}

export const engineService = {
  manifests: async (): Promise<EngineManifestRow[]> => {
    const response = await apiClient.get('/engines/manifests')
    return response.data.data.engines
  },

  listEngines: async (): Promise<Array<{ name: string; filename: string; path: string }>> => {
    const response = await apiClient.get('/engines/list')
    return response.data.data
  },

  getRequirements: async (engine: string): Promise<EngineRequirements | null> => {
    try {
      const response = await apiClient.get(`/engines/${encodeURIComponent(engine)}/requirements`)
      return response.data.data
    } catch (error) {
      const status = typeof error === 'object' && error !== null && 'response' in error
        ? (error as { response?: { status?: number } }).response?.status
        : undefined
      if (status === 404) return null
      throw error
    }
  },

  resolveSelection: async (engine: string, version: string, selectedModules: string[]): Promise<EngineSelection> => {
    const response = await apiClient.post(`/engines/${encodeURIComponent(engine)}/selection`, {
      version,
      selectedModules,
    })
    return response.data.data
  },
}

export const settingsService = {
  getSetting: async (key: string): Promise<string | null> => {
    const response = await apiClient.get(`/settings/${key}`)
    return response.data.data?.value || null
  },

  setSetting: async (key: string, value: string, description?: string): Promise<void> => {
    await apiClient.put(`/settings/${key}`, { value, description })
  },

  getAllSettings: async (): Promise<Record<string, string>> => {
    const response = await apiClient.get('/settings')
    return response.data.data || {}
  },
}

export const permissionService = {
  getAgentPermissions: async (agentId: string): Promise<AgentPermissionsInfo> => {
    const response = await apiClient.get(`/agents/${agentId}/permissions`)
    return response.data.data
  },

  setVisibility: async (agentId: string, visibility: Visibility): Promise<void> => {
    await apiClient.put(`/agents/${agentId}/visibility`, { visibility })
  },

  setPermission: async (agentId: string, userId: string, permissionLevel: PermissionLevel): Promise<void> => {
    await apiClient.post(`/agents/${agentId}/permissions`, { userId, permissionLevel })
  },

  removePermission: async (agentId: string, userId: string): Promise<void> => {
    await apiClient.delete(`/agents/${agentId}/permissions/${userId}`)
  },

  transferOwnership: async (agentId: string, newOwnerId: string): Promise<void> => {
    await apiClient.post(`/agents/${agentId}/transfer-ownership`, { newOwnerId })
  },

  searchUsers: async (searchTerm: string, limit: number = 10): Promise<any[]> => {
    const response = await apiClient.get(`/users/search?q=${encodeURIComponent(searchTerm)}&limit=${limit}`)
    return response.data.data
  },

  getDiscoverableAgents: async (searchTerm?: string): Promise<Agent[]> => {
    const url = searchTerm 
      ? `/agents/discover?q=${encodeURIComponent(searchTerm)}`
      : '/agents/discover'
    const response = await apiClient.get(url)
    return response.data.data
  },
}

export const authService = {
  login: async (email: string, password: string) => {
    const response = await apiClient.post('/auth/login', { email, password })
    return response.data
  },

  register: async (userData: { username: string; email: string; password: string; name: string }) => {
    const response = await apiClient.post('/auth/register', userData)
    return response.data
  },

  getCurrentUser: async () => {
    const response = await apiClient.get('/auth/me')
    return response.data
  },

  personalTokenStatus: async (): Promise<{ active: boolean; createdAt: string | null }> => {
    const response = await apiClient.get('/auth/personal-token')
    return response.data
  },

  createPersonalToken: async (): Promise<{ token: string; createdAt: string }> => {
    const response = await apiClient.post('/auth/personal-token')
    return response.data
  },

  revokePersonalToken: async (): Promise<void> => {
    await apiClient.delete('/auth/personal-token')
  },

  getGoogleAuthUrl: async () => {
    const response = await apiClient.get('/auth/google/url')
    return response.data
  },
}

export interface User {
  id: string
  email: string
  username: string
  name: string
  avatar?: string
  role: 'guest' | 'user' | 'admin'
  provider: 'local' | 'google'
  createdAt: Date
  updatedAt: Date
}

export const userService = {
  getAllUsers: async (): Promise<User[]> => {
    const response = await apiClient.get('/users')
    return response.data.data
  },

  updateUserRole: async (userId: string, role: 'guest' | 'user' | 'admin'): Promise<User> => {
    const response = await apiClient.put(`/users/${userId}/role`, { role })
    return response.data.data
  },
}

export const notificationService = {
  getNotifications: async (): Promise<{ notifications: any[] }> => {
    const response = await apiClient.get('/notifications')
    return response.data
  },

  markAsRead: async (id: string): Promise<void> => {
    await apiClient.post(`/notifications/${id}/read`)
  },

  markAllAsRead: async (): Promise<void> => {
    await apiClient.post('/notifications/read-all')
  },

  deleteNotification: async (id: string): Promise<void> => {
    await apiClient.delete(`/notifications/${id}`)
  },

  // * API Keys management
  getApiKeys: async (): Promise<ApiKeyListItem[]> => {
    const response = await apiClient.get('/api-keys')
    return response.data.data
  },

  createApiKey: async (input: CreateApiKeyInput): Promise<ApiKey> => {
    const response = await apiClient.post('/api-keys', input)
    return response.data.data
  },

  getApiKeyById: async (id: string): Promise<ApiKey & { decryptedKey: string }> => {
    const response = await apiClient.get(`/api-keys/${id}`)
    return response.data.data
  },

  deleteApiKey: async (id: string): Promise<void> => {
    await apiClient.delete(`/api-keys/${id}`)
  },

  getApiKeyByProvider: async (provider: string): Promise<{ provider: string; key: string }> => {
    const response = await apiClient.get(`/api-keys/provider/${provider}`)
    return response.data.data
  },
}

export const apiService = {
  getApiKeys: async (): Promise<ApiKeyListItem[]> => {
    const response = await apiClient.get('/api-keys')
    return response.data.data
  },

  createApiKey: async (input: CreateApiKeyInput): Promise<ApiKey> => {
    const response = await apiClient.post('/api-keys', input)
    return response.data.data
  },

  getApiKeyById: async (id: string): Promise<ApiKey & { decryptedKey: string }> => {
    const response = await apiClient.get(`/api-keys/${id}`)
    return response.data.data
  },

  deleteApiKey: async (id: string): Promise<void> => {
    await apiClient.delete(`/api-keys/${id}`)
  },

  getApiKeysByProvider: async (provider: string): Promise<ApiKeyListItem[]> => {
    const response = await apiClient.get(`/api-keys/provider/${provider}`)
    return response.data.data
  },
}

export interface SecretAgentRef {
  id: string | null
  name: string | null
  containerUuid: string
}

export interface SecretListItem {
  id: string
  purpose: string
  origin: string
  provider: string
  keyType: string
  keyPreview: string
  description?: string
  createdAt: string
  agents: SecretAgentRef[]
}

export const secretService = {
  list: async (): Promise<SecretListItem[]> => {
    const response = await apiClient.get('/secrets')
    return response.data.data
  },
  rotate: async (id: string, key: string): Promise<{ id: string; keyPreview: string }> => {
    const response = await apiClient.post(`/secrets/${id}/rotate`, { key })
    return response.data.data
  },
  revoke: async (id: string): Promise<void> => {
    await apiClient.post(`/secrets/${id}/revoke`)
  },
}

// * Types for API Keys
export interface ApiKey {
  id: string
  userId: string
  keyType: string
  provider: string
  encryptedKey: string
  keyPreview: string
  description?: string
  createdAt: Date
  updatedAt: Date
}

export interface ApiKeyListItem {
  id: string
  keyType: string
  provider: string
  keyPreview: string
  description?: string
  createdAt: Date
}

export interface CreateApiKeyInput {
  keyType: string
  provider: string
  key: string
  description?: string
}

// * Intelligence Lab types
export interface LabAgentRuntime {
  dedicated: boolean
  endpoint?: string
  status: 'healthy' | 'unhealthy' | 'unknown'
}

export interface LabAgentApiKey {
  source: 'env' | 'console' | 'missing'
  envName?: string
  masked?: string
}

export interface LabAgentPrompt {
  exists: boolean
  source: 'runtime' | 'library' | 'console' | null
  path?: string
}

export interface LabRosterAgent {
  id: string
  name: string
  role?: string
  focus?: string
  kind?: string
  // * Le roster d'appartenance : 'integria' (le collectif réparti en pools) ou
  // * 'service' (agent logique autonome, sans pool). C'était déclaré string[],
  // * ce qui laissait croire à une liste d'identifiants.
  roster?: string | null
  enabled: boolean
  model?: string | null
  interactions?: string[]
  notes?: string | null
  level?: number | null
  capabilities?: string[]
  activationTerms?: string[]
  poolId?: string | null
  runtimeId?: string | null
  container?: string | null
  runtime: LabAgentRuntime
  apiKey: LabAgentApiKey
  prompt: LabAgentPrompt
}

export interface LabRosterPool {
  id: string
  service: string
  agentIds: string[]
}

// * Le Lab répond par une enveloppe, pas par le tableau lui-même.
export interface LabRoster {
  success: boolean
  environment?: string
  labEnabled?: boolean
  maxWorkers?: number
  pools: LabRosterPool[]
  agents: LabRosterAgent[]
}

export interface LabUsageTotals {
  calls: number
  inputTokens: number
  outputTokens: number
  cachedInputTokens?: number
  cacheWriteTokens: number
  cacheReadTokens: number
  totalTokens: number
  estimatedCostUsd: number
  unpricedCalls?: number
  isEstimateComplete?: boolean
}

export interface LabUsageAgent extends LabUsageTotals {
  agentId: string
  lastUsedAt?: string
}

export interface LabUsageDailyAgent extends LabUsageTotals {
  agentId: string
}

export interface LabUsageDay extends Partial<LabUsageTotals> {
  date: string
  agents: LabUsageDailyAgent[]
}

export interface LabUsageAdmin {
  month: string
  currency: string
  totals: LabUsageTotals
  daily: LabUsageDay[]
  agents: LabUsageAgent[]
  accounts?: unknown[]
  unassigned?: unknown
}

export interface PricingCatalogRow {
  provider: 'deepseek' | 'anthropic' | 'openai'
  model: string
  aliases: string[]
  effectiveFrom: string
  inputPerMTokens: number
  cachePerMTokens: number
  outputPerMTokens: number
}

export interface PricingCatalog {
  success: boolean
  currency: string
  unitTokens: number
  checkedAt: string
  rows: PricingCatalogRow[]
}

export const intelligenceLabService = {
  getStatus: async (): Promise<{ success: boolean; configured: boolean; baseUrlConfigured: boolean }> => {
    const response = await apiClient.get('/intelligence/lab/status')
    return response.data
  },

  // * ATF_Core relaie la réponse du Lab telle quelle : une enveloppe
  // * { success, pools, agents }. Elle était rendue comme si elle était déjà le
  // * tableau d'agents, et l'appelant s'écrasait sur « n is not iterable » sans
  // * rien indiquer de la vraie cause. On déballe ici, et on vérifie plutôt que
  // * de supposer : si le Lab change de forme, le message le dit.
  getRoster: async (): Promise<LabRoster> => {
    const response = await apiClient.get('/intelligence/lab/roster')
    const payload = response.data as Partial<LabRoster> | null

    if (!payload || !Array.isArray(payload.agents)) {
      throw new Error(
        "Réponse inattendue du Lab sur /intelligence/lab/roster : aucun tableau " +
        "« agents ». Vérifier INTELLIGENCE_LAB_BASE_URL et la version du Lab.",
      )
    }

    return {
      ...payload,
      success: payload.success ?? true,
      pools: Array.isArray(payload.pools) ? payload.pools : [],
      agents: payload.agents,
    }
  },

  getUsageAdmin: async (month: string): Promise<LabUsageAdmin> => {
    const response = await apiClient.get(`/intelligence/lab/usage-admin?month=${encodeURIComponent(month)}`)
    return response.data
  },

  getPricingCatalog: async (): Promise<PricingCatalog> => {
    const response = await apiClient.get('/intelligence/lab/pricing')
    const payload = response.data as Partial<PricingCatalog> | null
    if (
      !payload ||
      !Array.isArray(payload.rows) ||
      payload.currency !== 'USD' ||
      payload.unitTokens !== 1_000_000 ||
      typeof payload.checkedAt !== 'string'
    ) {
      throw new Error('Réponse inattendue du catalogue de prix.')
    }
    return {
      success: payload.success ?? true,
      currency: payload.currency,
      unitTokens: payload.unitTokens,
      checkedAt: payload.checkedAt,
      rows: payload.rows,
    }
  },

  savePricingCatalog: async (rows: PricingCatalogRow[]): Promise<PricingCatalog> => {
    const response = await apiClient.put('/intelligence/lab/pricing', { rows })
    return response.data
  },
}

export interface AgentUsageTotals {
  inputTokens: number
  outputTokens: number
  reports: number
}

export interface AgentUsageBySecret extends AgentUsageTotals {
  secretId: string
  purpose: string
  keyPreview: string
}

export interface AgentUsageByAgent extends AgentUsageTotals {
  agentId: string
  agentName: string
}

export interface AgentUsageSummary {
  month: string
  totals: AgentUsageTotals
  bySecret: AgentUsageBySecret[]
  byAgent: AgentUsageByAgent[]
}

export type StatsGrain = 'hour' | 'day' | 'week' | 'month' | 'year'
export type StatsScope = 'all' | 'team' | 'agent'

export interface StatsPoint {
  bucketStart: string
  bucketEnd: string
  models: number
  instances: number
  calls: number
  inputTokens: number
  outputTokens: number
  cacheTokens: null
  totalTokens: number
  secrets: number
}

export interface StatsFollowUp {
  grain: StatsGrain
  cursor: string
  start: string
  end: string
  agents: Array<{ id: string; name: string }>
  teams: Array<{ id: string; name: string }>
  rows: StatsPoint[]
}

export const agentUsageService = {
  getStats: async (query: {
    grain: StatsGrain
    cursor: string
    scope: StatsScope
    teamId?: string
    agentName?: string
  }): Promise<StatsFollowUp> => {
    const params = new URLSearchParams({
      grain: query.grain,
      cursor: query.cursor,
      scope: query.scope,
    })
    if (query.teamId) params.set('teamId', query.teamId)
    if (query.agentName) params.set('agentName', query.agentName)
    const response = await apiClient.get(`/usage/stats?${params.toString()}`)
    const payload = response.data?.data as StatsFollowUp | null
    if (!payload?.rows || !Array.isArray(payload.rows)) {
      throw new Error('Réponse inattendue du suivi.')
    }
    return payload
  },

  getSummary: async (month: string): Promise<AgentUsageSummary> => {
    const response = await apiClient.get(`/usage/tokens?month=${encodeURIComponent(month)}`)
    const payload = response.data?.data as Partial<AgentUsageSummary> | null
    if (!payload?.totals || !Array.isArray(payload.bySecret) || !Array.isArray(payload.byAgent)) {
      throw new Error('Réponse inattendue de la consommation des agents.')
    }
    return {
      month: payload.month ?? month,
      totals: payload.totals,
      bySecret: payload.bySecret,
      byAgent: payload.byAgent,
    }
  },
}

// * Portable model file. The Core route accepts this document directly.
export interface DefinitionDocument {
  format: 'atf.definitions'
  version: 1
  catalog: Array<{ name: string; description: string; channel: string | null }>
  agents: unknown[]
  teams: unknown[]
}

export interface DefinitionImportReport {
  mode: 'replace' | 'create'
  catalog: { upserted: string[]; skipped: string[] }
  agents: { created: string[]; updated: string[] }
  teams: { created: string[]; updated: string[] }
}

export const definitionService = {
  exportDefinitions: async (include: string): Promise<DefinitionDocument> => {
    const response = await apiClient.get('/definitions/export', { params: { include } })
    return response.data.data
  },

  importDefinitions: async (document: unknown): Promise<DefinitionImportReport> => {
    const response = await apiClient.post('/definitions/import', document, { params: { mode: 'replace' } })
    return response.data.data
  },

  exportTeamPackage: async (teamId: string): Promise<Blob> => {
    const response = await apiClient.get(`/definitions/teams/${teamId}/package`, { responseType: 'blob' })
    return response.data as Blob
  },
}

